// One metric result drawn the way its breakdown asks: over time as a line, by pool or channel as bars, a single
// figure as a number. One series, so no legend; ink for the marks, seal red only for the key figure. Every chart
// carries a visually hidden table, and each point or bar shows its figure on hover or focus.
import { useId, useState } from "react";
import { formatValue, type MetricResult } from "../lib/performance";

const W = 480;
const H = 180;
const PAD = { top: 18, right: 16, bottom: 30, left: 44 };

function niceMax(v: number, unit: string): number {
  if (unit === "percent") return v <= 10 ? 10 : v <= 25 ? 25 : v <= 50 ? 50 : 100;
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

function shortLabel(label: string): string {
  return label.replace(/^w\/c /, "").replace(/ \d{4}$/, "");
}

function DataTable({ r, caption }: { r: MetricResult; caption: string }) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{r.by === "none" ? "Figure" : r.by === "pool" ? "Lead pool" : r.by === "channel" ? "Channel" : "Period"}</th>
          <th scope="col">{r.label}</th>
        </tr>
      </thead>
      <tbody>
        {r.rows.map((row) => (
          <tr key={row.key ?? row.label}>
            <th scope="row">{row.label}</th>
            <td>
              {formatValue(row.value, r.unit, r.currency)}
              {r.unit === "percent" && row.denominator ? ` (${row.numerator} of ${row.denominator})` : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LineChart({ r, label }: { r: MetricResult; label: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const pts = r.rows;
  const values = pts.map((p) => p.value ?? 0);
  const max = niceMax(Math.max(0, ...values), r.unit);
  const iw = W - PAD.left - PAD.right;
  const ih = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (pts.length <= 1 ? iw / 2 : (i * iw) / (pts.length - 1));
  const y = (v: number) => PAD.top + ih - (v / max) * ih;
  // A gap in the line where a period has no figure (a rate with nothing to divide by).
  const segments: string[] = [];
  let cur: string[] = [];
  pts.forEach((p, i) => {
    if (p.value === null || p.value === undefined) {
      if (cur.length) segments.push(cur.join(" "));
      cur = [];
    } else cur.push(`${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
  });
  if (cur.length) segments.push(cur.join(" "));
  const every = Math.max(1, Math.ceil(pts.length / 6));
  const last = [...pts.keys()].reverse().find((i) => pts[i]!.value !== null && pts[i]!.value !== undefined);
  const shown = hover ?? last ?? null;
  return (
    <svg className="perf-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line className={f === 0 ? "perf-axis" : "perf-grid"} x1={PAD.left} x2={W - PAD.right} y1={y(f * max)} y2={y(f * max)} />
          <text className="perf-tick" x={PAD.left - 6} y={y(f * max) + 3} textAnchor="end">
            {formatValue(f * max, r.unit, r.currency)}
          </text>
        </g>
      ))}
      {pts.map((p, i) =>
        i % every === 0 || i === pts.length - 1 ? (
          <text key={`x${i}`} className="perf-tick" x={x(i)} y={H - 10} textAnchor="middle">
            {shortLabel(p.label)}
          </text>
        ) : null,
      )}
      {segments.map((s, i) => (
        <polyline key={i} className="perf-line" points={s} />
      ))}
      {pts.map((p, i) =>
        p.value === null || p.value === undefined ? null : (
          <g key={`p${i}`}>
            <circle className={`perf-dot${i === shown ? " is-on" : ""}`} cx={x(i)} cy={y(p.value)} r={i === shown ? 5 : 3.5} />
            <rect
              className="perf-hit"
              x={x(i) - Math.max(8, iw / Math.max(1, pts.length) / 2)}
              y={PAD.top}
              width={Math.max(16, iw / Math.max(1, pts.length))}
              height={ih}
              onMouseEnter={() => setHover(i)}
            />
          </g>
        ),
      )}
      {shown !== null && pts[shown] && pts[shown]!.value !== null ? (
        <text className="perf-value" x={Math.min(Math.max(x(shown), PAD.left + 30), W - PAD.right - 30)} y={Math.max(12, y(pts[shown]!.value!) - 10)} textAnchor="middle">
          {formatValue(pts[shown]!.value, r.unit, r.currency)}
          {hover !== null ? ` · ${shortLabel(pts[shown]!.label)}` : ""}
        </text>
      ) : null}
    </svg>
  );
}

function Bars({ r }: { r: MetricResult }) {
  const rows = r.rows;
  const max = Math.max(0, ...rows.map((x) => x.value ?? 0));
  const top = rows.reduce<number | null>((best, x, i) => ((x.value ?? 0) > 0 && (best === null || (x.value ?? 0) > (rows[best]!.value ?? 0)) ? i : best), null);
  return (
    <ul className="perf-bars" aria-hidden="true">
      {rows.map((x, i) => (
        <li key={x.key ?? x.label} title={`${x.label}: ${formatValue(x.value, r.unit, r.currency)}`}>
          <span className="perf-bar-label">
            <span>{x.label}</span>
            <strong>{formatValue(x.value, r.unit, r.currency)}</strong>
          </span>
          <span className="perf-bar-track">
            <span className={`perf-bar-fill${i === top ? " is-key" : ""}`} style={{ width: max ? `${((x.value ?? 0) / max) * 100}%` : "0%" }} />
          </span>
        </li>
      ))}
    </ul>
  );
}

export function MetricChart({ result, caption }: { result: MetricResult; caption: string }) {
  const id = useId();
  const over = result.by === "week" || result.by === "month";
  const label = `${caption}. ${result.rows
    .map((x) => `${x.label}: ${formatValue(x.value, result.unit, result.currency)}`)
    .slice(0, 24)
    .join("; ")}`;
  return (
    <div className="perf-metric" aria-describedby={id}>
      {result.by === "none" ? (
        <p className="perf-figure">{formatValue(result.total, result.unit, result.currency)}</p>
      ) : over ? (
        <LineChart r={result} label={label} />
      ) : (
        <Bars r={result} />
      )}
      <div id={id}>
        <DataTable r={result} caption={caption} />
      </div>
    </div>
  );
}
